import { LoadingOutlined } from "@ant-design/icons"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../utils/cn.ts"
import type { ListStatus } from "../helpers/automation-list"
import { journeySteps, summarizeFormValues } from "../helpers/form-summary"
import type { AutomationFormValues } from "../helpers/form-values"
import { missingChoices } from "../helpers/section-completion"
import { useAutomationPreview } from "../hooks/use-automation-preview"
import type { AutomationOptions } from "../types/automation.types"
import type { BuilderAction } from "../types/builder-action"
import { StatusBadge } from "./builder/StatusBadge"
import { FullPreviewModal } from "./FullPreviewModal"
import { EmailPreview } from "./review/EmailPreview"
import { LeadPicker } from "./review/LeadPicker"
import { MissingChoices } from "./review/MissingChoices"
import { ReviewJourney } from "./review/ReviewJourney"

export const LIVE_REVIEW_ID = "automation-live-review"
const HEADING_ID = "automation-review-heading"

// Disabling stops new enrollments, and enabling again skips whatever fell due.
// "Activated", as the paused automation's own button says.
const HAS_PAUSE_NOTE: ReadonlySet<ListStatus> = new Set(["active", "paused"])

interface ReviewPanelProps {
  values: AutomationFormValues
  options: AutomationOptions
  /** The header's status, so both always agree. */
  status: ListStatus
  /** The page's in-flight action, if any. */
  busy: BuilderAction | null
  onFocusField: (field: keyof AutomationFormValues) => void
}

// The live review column: the rule, its journey and the email, all from the
// same values the form submits.
export function ReviewPanel({ values, options, status, busy, onFocusField }: ReviewPanelProps) {
  const { t } = useTranslation()
  const [leadId, setLeadId] = useState<string | null>(null)
  const [fullPreviewOpen, setFullPreviewOpen] = useState(false)
  const { preview, status: previewStatus, retry } = useAutomationPreview(values, options, leadId)

  // Computed locally so they track every keystroke without a round trip.
  const summary = useMemo(() => summarizeFormValues(t, values, options), [options, t, values])
  const steps = useMemo(
    () => journeySteps(t, values, options, summary),
    [options, summary, t, values]
  )
  const missing = useMemo(() => missingChoices(values, options), [options, values])

  const channelLabel =
    options.channels.find((item) => item.key === values.channel)?.label ?? values.channel
  const sender = options.senders.find((item) => item.id === values.sender_user)
  const pauseNote = HAS_PAUSE_NOTE.has(status)
    ? t(`settingsAutomation.review.pauseNote.${status}`)
    : null
  const disabled = busy !== null

  return (
    <section
      id={LIVE_REVIEW_ID}
      tabIndex={-1}
      aria-labelledby={HEADING_ID}
      className={cn(
        "flex min-w-0 flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4 outline-none focus-visible:ring-2 focus-visible:ring-primary",
        // Pinned under the settings header at xl, scrolling on its own when
        // taller than the space left.
        "xl:sticky xl:top-[7.5rem] xl:max-h-[calc(100svh-var(--header-height,64px)-9rem)] xl:overflow-y-auto"
      )}
    >
      <div className={"flex flex-col gap-2"}>
        <div className={"flex flex-wrap items-center justify-between gap-2"}>
          <h3 id={HEADING_ID} className={"m-0 text-start text-base font-semibold"}>
            {t("settingsAutomation.review.heading")}
          </h3>
          <div className={"flex items-center gap-3"}>
            <span
              data-testid={"automation-review-channel"}
              className={"rounded-full bg-primary-bg px-2 py-0.5 text-xs font-medium text-primary-strong"}
            >
              {channelLabel}
            </span>
            <span data-testid={"automation-review-status"}>
              <StatusBadge status={status} />
            </span>
          </div>
        </div>
        {pauseNote && (
          <p data-testid={"automation-review-pause-note"} className={"m-0 text-sm text-gray-600"}>
            {pauseNote}
          </p>
        )}
        {busy && (
          <p
            data-testid={"automation-review-busy"}
            className={"m-0 flex items-center gap-2 text-sm text-gray-600"}
          >
            <LoadingOutlined aria-hidden={"true"} />
            {t(`settingsAutomation.review.busy.${busy}`)}
          </p>
        )}
      </div>

      {/* aria-busy stays off the email preview, whose status is the panel's
          live region: a busy ancestor can hold its announcements back. */}
      <div
        data-testid={"automation-review-content"}
        aria-busy={disabled || undefined}
        className={cn("flex flex-col gap-4", disabled && "opacity-60")}
      >
        {missing.length > 0 && (
          <MissingChoices items={missing} disabled={disabled} onFocusField={onFocusField} />
        )}

        <p data-testid={"automation-summary"} className={"m-0"}>
          {summary.sentence}
        </p>

        <ReviewJourney steps={steps} />

        <LeadPicker value={leadId} disabled={disabled} onChange={setLeadId} />
      </div>

      <div className={cn(disabled && "opacity-60")}>
        <EmailPreview
          preview={preview}
          status={previewStatus}
          fallbackFrom={sender ? t("settingsAutomation.review.fromVia", { name: sender.name }) : "—"}
          fallbackSubject={values.subject.trim() || "—"}
          disabled={disabled}
          onRetry={retry}
          onOpenFull={() => setFullPreviewOpen(true)}
        />
      </div>

      <p className={"m-0 border-t border-gray-100 pt-3 text-sm text-gray-500"}>
        {t(`settingsAutomation.review.${status === "active" ? "live" : "notLive"}`)}
      </p>

      <FullPreviewModal
        open={fullPreviewOpen}
        preview={preview}
        onClose={() => setFullPreviewOpen(false)}
      />
    </section>
  )
}
